import { defineNuxtPlugin, useRuntimeConfig, useState } from 'nuxt/app'
import braintree from 'braintree-web'
import type { HostedFieldsField } from 'braintree-web/hosted-fields'

import type { PayPalCheckoutLoadPayPalSDKOptions } from 'braintree-web/paypal-checkout'

export interface Field extends HostedFieldsField {
  error?: string
}

export interface HostedFieldFieldOptions {
  cardholderName?: Field | undefined
  cvv?: Field | undefined
  expirationDate?: Field | undefined
  expirationMonth?: Field | undefined
  expirationYear?: Field | undefined
  number?: Field | undefined
  postalCode?: Field | undefined
}

export interface HostedFieldsCreateOptions {
  authorization?: string | undefined
  fields: HostedFieldFieldOptions
  styles?: any
}

export interface GooglePaymentCreateOptions {
  authorization?: string
  useDeferredClient: true
  googlePayVersion?: number
  googleMerchantId?: string
}

export interface GooglePaymentClientCreateOptions {
  paymentDataCallbacks?: google.payments.api.PaymentDataCallbacks
}

export default defineNuxtPlugin(async () => {
  const braintreeClient = useState<braintree.Client>()

  const googlePayEnvironment = useState<string>(() => 'TEST')

  const runtimeConfig = useRuntimeConfig()
  braintreeClient.value = await braintree.client.create({
    authorization: runtimeConfig.public.braintreeClientToken as string,
  })

  googlePayEnvironment.value = runtimeConfig.public.googlePayEnvironment as string

  const hostedFieldsInstance = (options: HostedFieldsCreateOptions) => {
    return braintree.hostedFields.create({
      ...options,
      client: braintreeClient.value,
    })
  }

  const paypalCheckoutInstance = async (
    options: PayPalCheckoutLoadPayPalSDKOptions,
  ) => {
    const paypalCheckoutInstance = await braintree.paypalCheckout.create({
      client: braintreeClient.value,
    })
    await paypalCheckoutInstance.loadPayPalSDK(options)
    return paypalCheckoutInstance
  }

  const googlePaymentInstance = async (options: GooglePaymentCreateOptions) => {
    const googlePaymentInstance = await braintree.googlePayment.create({
      ...options,
      client: braintreeClient.value,
    })

    return googlePaymentInstance
  }

  const googlePaymentClient = async ({
    paymentDataCallbacks,
  }: GooglePaymentClientCreateOptions) => {
    const clientConfig: any = {
      environment: googlePayEnvironment.value,
      paymentDataCallbacks,
    }
    const googlePaymentClient = new window.google.payments.api.PaymentsClient(
      clientConfig,
    )
    return googlePaymentClient
  }

  const applePayInstance = async () => {
    const applePayInstance = await braintree.applePay.create({
      client: braintreeClient.value,
    })
    return applePayInstance
  }

  return {
    provide: {
      hostedFieldsInstance,
      paypalCheckoutInstance,
      googlePaymentInstance,
      googlePaymentClient,
      applePayInstance,
    },
  }
})
